import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useAuth } from '@fourthwall/auth'
import type { PayoutApiClient } from '@fourthwall/services/dist/PayoutApiClient'

import { useShopPayoutQuery } from '../../../../../hooks/useShopPayoutQuery'
import { useDeletePayoutConnectionMutation } from '../queries/useDeletePayoutConnectionMutation'
import { useStripeOnboardingFlow } from './useStripeOnboardingFlow'

type PayoutInfoData = Awaited<ReturnType<typeof PayoutApiClient.getShopPayout>>['data']
type ActivePayoutInfo = Extract<PayoutInfoData, { status: 'ACTIVE' }>
type InProgressPayoutInfo = Extract<PayoutInfoData, { status: 'IN_PROGRESS' }>

const isActivePayoutInfo = (data: PayoutInfoData): data is ActivePayoutInfo =>
  data.status === 'ACTIVE'

const isInProgressPayoutInfo = (data: PayoutInfoData): data is InProgressPayoutInfo =>
  data.status === 'IN_PROGRESS'

export const usePayoutsSetup = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const { startStripeOnboarding, isLoading: isStripeOnboardingLoading } = useStripeOnboardingFlow()
  const { user } = useAuth()
  const { deletePayoutConnectionMutation } = useDeletePayoutConnectionMutation()
  const { shopPayoutQuery } = useShopPayoutQuery()

  const isUserTheAccountCreator =
    shopPayoutQuery.data &&
    (isActivePayoutInfo(shopPayoutQuery.data) || isInProgressPayoutInfo(shopPayoutQuery.data)) &&
    user?.email === shopPayoutQuery.data.owner.accountCreator.email

  const canUserEditPayouts =
    isUserTheAccountCreator &&
    hasPermission('settings.billing.payouts.edit') &&
    dashboardFeatureFlags.payoutSetup.enabled

  const canUserSetupPayouts =
    hasPermission('settings.billing.payouts.setup') && dashboardFeatureFlags.payoutSetup.enabled

  const canUserSetupNewPayouts = !isUserTheAccountCreator && canUserSetupPayouts

  const handleEdit = canUserEditPayouts ? startStripeOnboarding : undefined

  const handleNewSetup = canUserSetupNewPayouts
    ? () => {
        deletePayoutConnectionMutation.mutate([], {
          onSuccess: startStripeOnboarding,
        })
      }
    : undefined

  const getInfo = (data: ActivePayoutInfo | InProgressPayoutInfo) => {
    const { owner, connectedAt } = data

    return {
      partnerName: owner.type === 'PARTNER' ? owner.partnerName : undefined,
      ownerName: owner.accountCreator.fullName,
      ownerEmail: owner.accountCreator.email,
      date: connectedAt,
    }
  }

  const getPayoutsSetup = () => {
    if (shopPayoutQuery.isSuccess && shopPayoutQuery.data.status !== 'INACTIVE') {
      return {
        status: 'ACTIVE' as const,
        inProgress: shopPayoutQuery.data.status === 'IN_PROGRESS',
        info: getInfo(shopPayoutQuery.data),
        onEdit: handleEdit,
        onNewSetup: handleNewSetup,
      }
    }

    return {
      status: 'INACTIVE' as const,
      onSetup: canUserSetupPayouts ? startStripeOnboarding : undefined,
    }
  }

  return {
    setup: getPayoutsSetup(),
    isLoading: deletePayoutConnectionMutation.isLoading || isStripeOnboardingLoading,
  }
}
